import React, { useState } from "react";
import { EditIcon, TrashIcon } from "../icons";
import { useToast } from "../../contexts/ToastContext";
import { useHotel } from "../../contexts/HotelContext";
import { updateFile, archiveFile, restoreFile } from "../../services/api";
import EditFileModal from "./EditFileModal";

interface FileItem {
  id: string;
  displayName: string;
  description?: string;
  originalFileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  category: string;
  defaultNote?: string;
  metadata?: {
    mimeType?: string;
    tags?: string[];
  };
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface FileRowProps {
  file: FileItem;
  onRefresh: () => void;
  getFileTypeIcon: (fileType: string) => React.ReactNode;
  getCategoryLabel: (category: string) => string;
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  onRefresh,
  getFileTypeIcon,
  getCategoryLabel,
  formatFileSize,
  formatDate,
}) => {
  const { user } = useHotel();
  const currentHotel = user?.defaultHotel;
  const { addToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleModalSave = async (editData: {
    displayName: string;
    description: string;
    category: string;
  }) => {
    if (!currentHotel?.id) return;

    try {
      await updateFile(file.id, editData);
      addToast({
        type: "success",
        message: "Plik został zaktualizowany",
      });
      setIsModalOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Update error:", error);
      addToast({
        type: "error",
        message: "Wystąpił błąd podczas aktualizacji pliku",
      });
    }
  };

  const handleArchive = async () => {
    if (!currentHotel?.id) return;
    if (!window.confirm("Czy na pewno chcesz zarchiwizować ten plik?")) return;

    setIsLoading(true);
    try {
      await archiveFile(file.id);
      addToast({
        type: "success",
        message: "Plik został zarchiwizowany",
      });
      onRefresh();
    } catch (error) {
      console.error("Archive error:", error);
      addToast({
        type: "error",
        message: "Wystąpił błąd podczas archiwizacji pliku",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!currentHotel?.id) return;

    setIsLoading(true);
    try {
      await restoreFile(file.id);
      addToast({
        type: "success",
        message: "Plik został przywrócony z archiwum",
      });
      onRefresh();
    } catch (error) {
      console.error("Restore error:", error);
      addToast({
        type: "error",
        message: "Wystąpił błąd podczas przywracania pliku",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = file.fileUrl;
    link.download = file.originalFileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <tr className={file.isArchived ? "bg-gray-50 opacity-60" : ""}>
        {/* Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            {getFileTypeIcon(file.fileType)}
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">
                {file.displayName}
              </div>
              {file.description && (
                <div className="text-sm text-gray-500">{file.description}</div>
              )}
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {file.fileType.toUpperCase()}
        </td>

        {/* Size */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatFileSize(file.fileSize)}
        </td>

        {/* Category */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
            {getCategoryLabel(file.category)}
          </span>
        </td>

        {/* Author */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {file.uploadedBy.firstName} {file.uploadedBy.lastName}
        </td>

        {/* Date */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatDate(file.createdAt)}
        </td>

        {/* Actions */}
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="text-primary-700 hover:text-primary-900"
              title="Pobierz plik"
            >
              ⬇
            </button>
            <button
              onClick={handleEdit}
              className="text-gray-600 hover:text-gray-900"
              title="Edytuj plik"
            >
              <EditIcon className="h-4 w-4" />
            </button>
            {file.isArchived ? (
              <button
                onClick={handleRestore}
                disabled={isLoading}
                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                title="Przywróć z archiwum"
              >
                ↻
              </button>
            ) : (
              <button
                onClick={handleArchive}
                disabled={isLoading}
                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                title="Archiwizuj plik"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* EditFileModal */}
      <EditFileModal
        file={file}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
        isLoading={isLoading}
      />
    </>
  );
};

export default FileRow;
